/* eslint-disable react/prop-types */
import {useContext, useEffect, useState} from 'react'

import cx from 'classnames'

import Context from '@s-ui/react-context'
import {useOnScreen} from '@s-ui/react-hooks'
import {useRouter} from '@s-ui/react-router'
import ListCarousel from '@adv-mt/mt-list-carousel'
import ListLatestModels from '@adv-mt/mt-list-latest-models'
import TitleBasic, {titleBasicSizes, titleBasicTags} from '@adv-mt/mt-title-basic'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

const BASE_CLASS = 'mt-AdSpace'
const DEFAULT_SPACE_POSITION = 10
const PATH_SUBSCRIPTION = '/renting/'

const SubscriptionCarouselTitle = ({i18n}) => {
  return (
    <div className="mt-ListCarouselTitle">
      <TitleBasic
        as={titleBasicTags.h3}
        description={i18n.t('SUBSCRIPTION.CLAIM.NEW_OWNERSHIP')}
        size={titleBasicSizes.xxs}
        title={i18n.t('SUBSCRIPTION.CLAIM.DISCOVER')}
      />
    </div>
  )
}

const getSubscriptionCarouselItems = ({
  i18n,
  subscriptionCarouselResultsItems = [],
  trackAndNavigateToSubscriptionAd
}) =>
  subscriptionCarouselResultsItems.map(ad => {
    const {imgUrl, url, monthlySubscriptionFee, title, year} = ad

    return {
      badgeText: i18n.t('COMMON.RENTING'),
      description: title,
      imageUrl: imgUrl,
      caption: year,
      onCardClick: () => trackAndNavigateToSubscriptionAd(ad),
      title: i18n.t('COMMON.MONTHLY_FEE', {
        amount: i18n.c(monthlySubscriptionFee)
      }),
      url
    }
  })

const TrackModelDetailsAdImpressionsCollected = ({children, latestModels, onMount}) => {
  const {domain} = useContext(Context)
  const {track: analyticsTrack} = useAnalyticsTrack()
  const config = domain.get('config')

  const {TRACKING_EVENT_CONTEXTS, TRACKING_EVENT_NAMES} = config.get('tracking')

  useEffect(() => {
    if (latestModels.length) {
      onMount()
      const eventData = domain.get('get_event_tracking_data_use_case').execute({
        context: TRACKING_EVENT_CONTEXTS.LIST_CAROUSEL_MODEL_DETAIL,
        event: TRACKING_EVENT_NAMES.AD_IMPRESSIONS_COLLECTED_EVENT,
        vehicles: latestModels
      })
      analyticsTrack(eventData)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestModels])

  return children
}

const _getSubscriptionCarousel = async ({domain, initialSearch}) => {
  const [, response] = await domain.get('get_subscription_carousel_ads_use_case').execute(initialSearch)
  return response || {}
}

const TrackSubscriptionAdImpressionsCollected = ({
  children,
  subscriptionCarouselResults = [],
  hasMakeFilter,
  onMount
}) => {
  const {domain} = useContext(Context)
  const config = domain.get('config')

  const {track: analyticsTrack} = useAnalyticsTrack()

  const {TRACKING_EVENT_CONTEXTS, TRACKING_EVENT_NAMES} = config.get('tracking')

  useEffect(() => {
    if (subscriptionCarouselResults.length) {
      onMount()
      const eventData = domain.get('get_event_tracking_data_use_case').execute({
        ads: subscriptionCarouselResults,
        context: hasMakeFilter
          ? TRACKING_EVENT_CONTEXTS.LIST_CAROUSEL_SUBSCRIPTION_MAKE
          : TRACKING_EVENT_CONTEXTS.LIST_CAROUSEL_SUBSCRIPTION_RANDOM,
        event: TRACKING_EVENT_NAMES.AD_IMPRESSIONS_COLLECTED_EVENT
      })
      analyticsTrack(eventData)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionCarouselResults])

  return children
}

const useAdSpaces = ({offerTypeId, latestModels = [], initialSearch = {}}) => {
  const {domain, i18n} = useContext(Context)
  const router = useRouter()
  const config = domain.get('config')
  const {
    HAS_ADS_LIST_MODEL_DETAILS_CAROUSEL_ENABLED,
    HAS_SUBSCRIPTION_ADS_LIST_ENABLED,
    OFFER_TYPE_NEW,
    OFFER_TYPE_KM0
  } = config.get('search')
  const {HIT_INFORMATIONS: hitInformations, HIT_ORIGINS: hitOrigins} = config.get('tracking')
  const {TRACKING_EVENT_NAMES} = config.get('tracking')

  const {track: analyticsTrack} = useAnalyticsTrack()

  const isNewVehicleOrKm0 = offerTypeId === OFFER_TYPE_NEW || offerTypeId === OFFER_TYPE_KM0
  const [isListCarouselNearScreen, listCarouselRef] = useOnScreen()
  const [isListCarouselVisible, setIsListCarouselVisible] = useState(false)
  const [isAdSpaceVisible, setIsAdSpaceVisible] = useState(false)
  const [subscriptionCarouselResults, setSubscriptionCarouselResults] = useState({})

  const {items: subscriptionCarouselResultsItems, hasMakeFilter} = subscriptionCarouselResults

  useEffect(() => {
    isListCarouselNearScreen && setIsListCarouselVisible(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isListCarouselNearScreen])

  useEffect(() => {
    const updateSubscriptionCarousel = async () =>
      setSubscriptionCarouselResults(await _getSubscriptionCarousel({domain, initialSearch}))

    isListCarouselNearScreen && !isNewVehicleOrKm0 && updateSubscriptionCarousel()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isListCarouselNearScreen])

  const trackAndNavigateToSubscriptionAd = ad => {
    const {url} = ad
    analyticsTrack({
      event: TRACKING_EVENT_NAMES.SUBSCRIPTION_CAROUSEL_CLICKED_EVENT,
      data: {
        hit_information: hitInformations.SUBSCRIPTION_CAROUSEL_DETAIL_CLICKED,
        hit_origin: hitOrigins.SUBSCRIPTION_CAROUSEL_DETAIL_CLICKED
      }
    }).then(() => router.push(url))
  }

  const trackAndNavigateToSubscription = () =>
    analyticsTrack({
      event: TRACKING_EVENT_NAMES.SUBSCRIPTION_CAROUSEL_CLICKED_EVENT,
      data: {
        hit_information: hitInformations.SUBSCRIPTION_CAROUSEL_LISTING_CLICKED,
        hit_origin: hitOrigins.SUBSCRIPTION_CAROUSEL_DETAIL_CLICKED
      }
    }).then(() => (window.location.href = PATH_SUBSCRIPTION))

  const navigationOptionsSubscription = {
    ctaText: i18n.t('COMMON.EXPLORE'),
    label: i18n.t('SUBSCRIPTION.CTA.EXPLORE_OFFERINGS'),
    url: PATH_SUBSCRIPTION,
    onClick: trackAndNavigateToSubscription
  }

  const classesListCarousel = cx(BASE_CLASS, {
    [`is-hidden`]: !isAdSpaceVisible
  })

  const adSpace = {
    position: DEFAULT_SPACE_POSITION,
    component: (
      <div className={classesListCarousel} ref={listCarouselRef}>
        {isListCarouselVisible && isNewVehicleOrKm0 && HAS_ADS_LIST_MODEL_DETAILS_CAROUSEL_ENABLED && (
          <TrackModelDetailsAdImpressionsCollected
            latestModels={latestModels}
            onMount={() => setIsAdSpaceVisible(true)}
          >
            <ListLatestModels
              models={latestModels}
              navigationOptions={{
                label: i18n.t('LIST_CAROUSELS.NEW_MODELS.NAVIGATION')
              }}
              title={i18n.t('LIST_CAROUSELS.NEW_MODELS.TITLE')}
            />
          </TrackModelDetailsAdImpressionsCollected>
        )}
        {isListCarouselVisible && !isNewVehicleOrKm0 && HAS_SUBSCRIPTION_ADS_LIST_ENABLED && (
          <TrackSubscriptionAdImpressionsCollected
            subscriptionCarouselResults={subscriptionCarouselResultsItems}
            hasMakeFilter={hasMakeFilter}
            onMount={() => setIsAdSpaceVisible(true)}
          >
            <ListCarousel
              title={() => <SubscriptionCarouselTitle i18n={i18n} />}
              items={getSubscriptionCarouselItems({
                i18n,
                subscriptionCarouselResultsItems,
                trackAndNavigateToSubscriptionAd
              })}
              navigationOptions={navigationOptionsSubscription}
            />
          </TrackSubscriptionAdImpressionsCollected>
        )}
      </div>
    )
  }

  return [adSpace]
}

export {useAdSpaces}
